/**
 * EmpresaSite — landing pública `/empresa/{slug}` (v2 — com blocos ricos)
 */
import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Calendar as CalendarIcon, User, Star, Instagram, Facebook, MessageCircle,
  Clock, MapPin, Phone, Users, Award,
} from "lucide-react";
import { useApplyTenantBranding } from "@/contexts/branding";
import PublicStoreSection from "@/components/PublicStoreSection";

const API = process.env.REACT_APP_BACKEND_URL;
const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function EmpresaSite() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("_preview") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useApplyTenantBranding({
    primary_color: data?.primary_color,
    display_name: data?.display_name,
    favicon_url: data?.logo_url,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await axios.get(`${API}/api/public/landing/${slug}`);
        setData(d);
        // não registra view em modo preview
        if (!isPreview) {
          axios.post(`${API}/api/public/landing/${slug}/view`).catch(() => {});
        } else {
          // Avisa o pai que o iframe está pronto pra receber overrides
          try {
            window.parent?.postMessage({ type: "landing-preview-ready", slug }, "*");
          } catch { /* silencia */ }
        }
      } catch (e) {
        setError(e?.response?.data?.detail || "Empresa não encontrada");
      }
    })();
  }, [slug, isPreview]);

  // Modo preview: escuta o editor e sobrescreve campos editáveis ao vivo
  useEffect(() => {
    if (!isPreview) return undefined;
    const onMsg = (evt) => {
      const msg = evt.data;
      if (!msg || msg.type !== "landing-preview-branding") return;
      const b = msg.branding || {};
      setData((prev) => (prev ? {
        ...prev,
        headline: b.headline ?? prev.headline,
        cover_url: b.cover_url ?? prev.cover_url,
        about: b.about ?? prev.about,
        published: b.landing_published !== false,
        landing_display_mode: b.landing_display_mode ?? prev.landing_display_mode ?? "hybrid",
        social: {
          ...(prev.social || {}),
          instagram: b.instagram ?? prev.social?.instagram,
          facebook: b.facebook ?? prev.social?.facebook,
          tiktok: b.tiktok ?? prev.social?.tiktok,
          whatsapp: b.whatsapp_business ?? prev.social?.whatsapp,
          google_review: b.google_review_url ?? prev.social?.google_review,
        },
      } : prev));
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [isPreview]);

  if (error) return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
      <div className="text-center"><div className="text-4xl mb-3">🔍</div>
        <div className="font-display text-xl">{error}</div></div>
    </div>
  );
  if (!data) return <div className="min-h-screen grid place-items-center text-muted-foreground">Carregando…</div>;

  if (data.published === false) return (
    <div className="min-h-screen grid place-items-center bg-background text-foreground p-6">
      <div className="text-center max-w-md">
        <div className="text-4xl mb-3">🚧</div>
        <h1 className="font-display text-2xl">{data.display_name}</h1>
        <p className="text-sm text-muted-foreground mt-2">O site desta empresa está temporariamente fora do ar.</p>
      </div>
    </div>
  );

  const {
    display_name, logo_url, cover_url, headline, tagline, about, website_url,
    address, phone, social = {}, featured_services = [],
    business_hours = {}, is_open, next_open_hint,
    nps, testimonial, years_in_business,
    landing_display_mode = "hybrid",
  } = data;
  const showBooking = landing_display_mode !== "store_only";
  const showStore   = landing_display_mode !== "booking_only";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="empresa-site">
      {cover_url ? (
        <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden" data-testid="site-cover">
          <img src={cover_url} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-background" />
        </div>
      ) : (
        <div className="h-40 sm:h-56" style={{ background: `linear-gradient(135deg, hsl(var(--tenant-primary)) 0%, hsl(var(--tenant-primary) / 0.6) 100%)` }} />
      )}

      <div className="max-w-4xl mx-auto px-6 -mt-16 sm:-mt-20 pb-20 relative">
        <div className="text-center">
          {logo_url && <img src={logo_url} alt="Logo" data-testid="site-logo"
            className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl mx-auto shadow-xl border-4 border-background object-cover" />}
          {tagline && <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mt-5" data-testid="site-tagline">{tagline}</div>}
          <h1 className="font-display text-3xl sm:text-5xl font-bold tracking-tight mt-2" data-testid="site-name">{display_name}</h1>
          {headline && <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mt-3" data-testid="site-headline">{headline}</p>}
          {website_url && <a href={website_url.startsWith("http") ? website_url : `https://${website_url}`}
             target="_blank" rel="noreferrer" data-testid="site-website-url"
             className="inline-block text-sm mt-3 underline underline-offset-4 hover:opacity-80"
             style={{ color: "hsl(var(--tenant-primary))" }}>{website_url}</a>}

          {(social.instagram || social.facebook || social.tiktok || social.whatsapp || social.google_review) && (
            <div className="flex items-center justify-center gap-3 mt-5" data-testid="site-socials">
              {social.instagram && <SocialIcon href={`https://instagram.com/${social.instagram.replace("@", "")}`} testid="social-instagram"><Instagram className="w-4 h-4" /></SocialIcon>}
              {social.facebook && <SocialIcon href={`https://facebook.com/${social.facebook}`} testid="social-facebook"><Facebook className="w-4 h-4" /></SocialIcon>}
              {social.tiktok && <SocialIcon href={`https://tiktok.com/@${social.tiktok.replace("@", "")}`} testid="social-tiktok"><TikTokIcon /></SocialIcon>}
              {social.whatsapp && <SocialIcon href={`https://wa.me/${social.whatsapp.replace(/\D/g, "")}`} testid="social-whatsapp"><MessageCircle className="w-4 h-4" /></SocialIcon>}
              {social.google_review && <SocialIcon href={social.google_review} testid="social-google" title="Avalie-nos no Google"><Star className="w-4 h-4" /></SocialIcon>}
            </div>
          )}

          <div className="flex items-center justify-center gap-2 flex-wrap mt-5" data-testid="site-badges">
            {nps && nps.count >= 5 && (
              <SBadge testid="badge-nps"><Star className="w-3 h-3 fill-current" /><strong>{nps.stars}</strong>
                <span className="opacity-75">· {nps.count} avaliações</span></SBadge>
            )}
            {years_in_business && years_in_business >= 1 && (
              <SBadge testid="badge-years"><Award className="w-3 h-3" /><strong>{years_in_business}</strong>
                <span className="opacity-75">{years_in_business === 1 ? "ano" : "anos"} no ar</span></SBadge>
            )}
            <SBadge testid="badge-status" tone={is_open ? "green" : "muted"}>
              <span className={`w-1.5 h-1.5 rounded-full ${is_open ? "bg-emerald-500" : "bg-muted-foreground"}`} />
              {is_open ? "Aberto agora" : next_open_hint || "Fechado"}
            </SBadge>
          </div>

          {showBooking && (
            <Link to={`/p/${slug}/agendar`} data-testid="site-cta-book"
              className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-shadow"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <CalendarIcon className="w-4 h-4" />{is_open ? "Agendar agora" : "Reservar horário"}
            </Link>
          )}
          {showStore && !showBooking && (
            <Link to={`/p/${slug}/agendar`} data-testid="site-cta-store"
              className="inline-flex items-center gap-2 mt-7 px-8 py-3.5 rounded-xl font-semibold text-base shadow-lg hover:shadow-xl transition-shadow"
              style={{ background: `hsl(var(--tenant-primary))`, color: `hsl(var(--tenant-primary-foreground))` }}>
              <CalendarIcon className="w-4 h-4" />Ver produtos
            </Link>
          )}
        </div>

        {showBooking && featured_services.length > 0 && (
          <section className="mt-14" data-testid="site-services">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground text-center mb-4">Destaques</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {featured_services.map((s) => (
                <Link key={s.id} to={`/p/${slug}/agendar?service=${s.id}`} data-testid={`site-service-${s.id}`}
                  className="group border border-border rounded-xl p-4 bg-card hover:border-[hsl(var(--tenant-primary))] hover:shadow-md transition-all">
                  <div className="w-8 h-1.5 rounded-full mb-3" style={{ background: `hsl(${s.color || "var(--tenant-primary)"})` }} />
                  <div className="font-medium text-sm truncate">{s.name}</div>
                  <div className="text-xs text-muted-foreground mt-1">{s.duration_minutes} min</div>
                  <div className="text-lg font-display font-semibold mt-2" style={{ color: "hsl(var(--tenant-primary))" }}>R$ {(s.price || 0).toFixed(2)}</div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {showStore && (
          <section className="mt-14" data-testid="site-store">
            <PublicStoreSection slug={slug} />
          </section>
        )}

        {testimonial && (
          <section className="mt-14" data-testid="site-testimonial">
            <div className="border border-border rounded-xl p-6 bg-card text-center max-w-2xl mx-auto">
              <div className="flex items-center justify-center gap-1 mb-3">
                {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 fill-amber-500 text-amber-500" />)}
              </div>
              <p className="italic text-foreground/85">&ldquo;{testimonial.comment}&rdquo;</p>
              <div className="text-xs text-muted-foreground mt-3">— {testimonial.author}</div>
            </div>
          </section>
        )}

        {about && (
          <section className="mt-14 max-w-2xl mx-auto text-center" data-testid="site-about">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-3">Sobre</div>
            <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{about}</p>
          </section>
        )}

        <section className="mt-14 grid sm:grid-cols-3 gap-4 text-sm">
          {Object.keys(business_hours).length > 0 && (
            <div className="border border-border rounded-lg p-4" data-testid="site-hours">
              <div className="flex items-center gap-2 mb-2"><Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-semibold text-xs uppercase tracking-wider">Horário</span></div>
              <ul className="text-xs space-y-1">
                {[0,1,2,3,4,5,6].map((wd) => {
                  const bh = business_hours[String(wd)];
                  return <li key={wd} className="flex justify-between"><span className="text-muted-foreground">{DAYS[wd]}</span>
                    <span>{Array.isArray(bh) ? `${String(bh[0]).padStart(2,"0")}:00 – ${String(bh[1]).padStart(2,"0")}:00` : "Fechado"}</span></li>;
                })}
              </ul>
            </div>
          )}
          {address && (
            <div className="border border-border rounded-lg p-4" data-testid="site-address">
              <div className="flex items-center gap-2 mb-2"><MapPin className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-semibold text-xs uppercase tracking-wider">Endereço</span></div>
              <div className="text-xs text-foreground/80">{address}</div>
              <a href={`https://www.google.com/maps/search/${encodeURIComponent(address)}`} target="_blank" rel="noreferrer"
                 className="text-xs mt-2 inline-block underline" style={{ color: "hsl(var(--tenant-primary))" }}>Como chegar →</a>
            </div>
          )}
          {phone && (
            <div className="border border-border rounded-lg p-4" data-testid="site-phone">
              <div className="flex items-center gap-2 mb-2"><Phone className="w-3.5 h-3.5 text-muted-foreground" />
                <span className="font-semibold text-xs uppercase tracking-wider">Contato</span></div>
              <div className="text-xs">{phone}</div>
            </div>
          )}
        </section>

        <section className="mt-14 pt-8 border-t border-border">
          <p className="text-center text-xs uppercase tracking-[0.25em] text-muted-foreground mb-4">Já conhece a {display_name}?</p>
          <div className="grid sm:grid-cols-2 gap-3 max-w-xl mx-auto">
            <Link to={`/portal/${slug}/cliente/login`} data-testid="site-portal-cliente"
              className="border border-border rounded-lg p-4 hover:border-[hsl(var(--tenant-primary))] transition-colors text-center">
              <User className="w-4 h-4 mx-auto mb-1.5" /><div className="text-sm font-medium">Sou cliente</div>
              <div className="text-xs text-muted-foreground">Entre com seu CPF</div>
            </Link>
            <Link to={`/t/${slug}/login`} data-testid="site-tenant-login"
              className="border border-border rounded-lg p-4 hover:border-[hsl(var(--tenant-primary))] transition-colors text-center">
              <Users className="w-4 h-4 mx-auto mb-1.5" /><div className="text-sm font-medium">Sou da equipe</div>
              <div className="text-xs text-muted-foreground">Acesso administrativo</div>
            </Link>
          </div>
        </section>

        <p className="text-center text-xs text-muted-foreground/60 mt-14">
          Powered by <span className="font-semibold">SUA PLATAFORMA</span>
        </p>
      </div>
    </div>
  );
}

function SocialIcon({ href, children, testid }) {
  return (
    <a href={href} target="_blank" rel="noreferrer" data-testid={testid}
       className="w-9 h-9 rounded-full border border-border grid place-items-center hover:border-[hsl(var(--tenant-primary))] hover:text-[hsl(var(--tenant-primary))] transition-colors">
      {children}
    </a>
  );
}

function SBadge({ children, tone, testid }) {
  const bg = tone === "green" ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-700"
    : tone === "muted" ? "bg-muted border-border text-muted-foreground"
    : "bg-[hsl(var(--tenant-primary)/0.08)] border-[hsl(var(--tenant-primary)/0.25)] text-[hsl(var(--tenant-primary))]";
  return <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${bg}`}>{children}</span>;
}

function TikTokIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.84-.1z" />
    </svg>
  );
}
